import type { ChangeEvent } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowDownToLine, FilePlus, Magnifier } from '@gravity-ui/icons';
import { Folder, NoSearchResults } from '@gravity-ui/illustrations';
import { Button, Icon, Popup, TextInput } from '@gravity-ui/uikit';
import { skipToken } from '@reduxjs/toolkit/query';
import type { MRT_SortingState } from 'material-react-table';

import { useNavigateReplace } from 'hooks';
import { useGetBlanksQuery, useInfoUserQuery, useLazyGetDownloadBlanksQuery } from 'store/api';
import { routes } from 'routes';
import { useMediaQuery } from '@mui/material';
import { AdaptiveBlankList } from './blank-list';
import { useSelector } from 'react-redux';
import type { AppState } from 'store/store';
import { BlankTableLoader } from './blank-table/blank-table-loader';
import { BlankTable } from './blank-table';
import {
	ADD_BLANK,
	BLANKS_LIST,
	DOWNLOAD_ALL,
	EXPORT_PDF,
	NO_DATA,
	NO_DATA_INFO,
	NO_DATA_ORG,
	NO_DATA_ORG_INFO,
	RESET_SEARCH,
	SEARCH,
} from './constants';
import { ProfileIcon } from 'features/ui-kit/profile-icon';
import styles from './styles.module.scss';

export const Blank = () => {
	const navigate = useNavigateReplace();
	const token = useSelector((state: AppState) => state.userState.token);
	const popupButtonRef = useRef<HTMLElement>(null);
	const isMobile = useMediaQuery('(max-width: 580px)');
	const [inputValue, setInputValue] = useState<string>('');
	const [openPopup, setOpenPopup] = useState<boolean>(false);
	const [pageSize, setPageSize] = useState<number>(isMobile ? 10 : 20);
	const [pageIndex, setPageIndex] = useState<number>(0);
	const [debouncedInputValue, setDebouncedInputValue] = useState<string>('');
	const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'id', desc: true }]);

	const [downloadBlanks] = useLazyGetDownloadBlanksQuery();

	const { data, isLoading, refetch } = useGetBlanksQuery(
		{
			pageNumber: pageIndex,
			pageSize: pageSize,
			sort: sorting[0]?.desc ? 'DESC' : 'ASC',
			sortField: sorting[0]?.id ?? 'id',
			name: debouncedInputValue ? debouncedInputValue : undefined,
		},
		{ refetchOnMountOrArgChange: true, refetchOnFocus: true, refetchOnReconnect: true },
	);

	const { data: response } = useInfoUserQuery(token ? null : skipToken);

	const role = response?.role;

	const isAdaptive = useMediaQuery('(max-width: 900px)');

	const accounts = data?.content;
	const totalItems = data?.totalElements;

	useMemo(() => {
		return data?.content;
	}, [data?.content]);

	const inputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		setInputValue(newValue.toLowerCase());
	};

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedInputValue(inputValue);
		}, 500);

		return () => {
			clearTimeout(handler);
		};
	}, [inputValue]);

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
			e.preventDefault();
			navigate(routes.new_blank.path);
		},
		[navigate],
	);

	const handleDownload = async () => {
		try {
			const blob = await downloadBlanks().unwrap();
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'blank.zip');
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (error) {
			console.error('Ошибка при скачивании бланков:', error);
			toast.error('Не удалось скачать бланки');
		}
	};

	return (
		<div className={styles.blank}>
			<div className={styles.blank__head}>
				{isMobile ? (
					<div className={styles.blank__head__title}>
						<div className={styles.blank__head__title__icon}>
							<ProfileIcon />
							<h2>{BLANKS_LIST}</h2>
						</div>

						<div className={styles.blank__head__title__actions}>
							<Button size="l" onClick={handleClick} view="flat">
								<Icon data={FilePlus} size={20} />
							</Button>
							<Button
								ref={popupButtonRef}
								onClick={() => setOpenPopup((prevOpen) => !prevOpen)}
								size="l"
								view="flat"
							>
								<Icon data={ArrowDownToLine} size={20} />
							</Button>
						</div>
					</div>
				) : (
					<h2>{BLANKS_LIST}</h2>
				)}

				<div className={styles.blank__head__menu}>
					<TextInput
						className={styles.blank__head__search}
						placeholder={SEARCH}
						size="l"
						startContent={<Icon data={Magnifier} size={16} />}
						hasClear
						value={inputValue}
						onChange={inputChange}
					/>

					{isAdaptive ? (
						<Button className={styles.blank__head__button} size="l" onClick={handleClick}>
							<Icon data={FilePlus} size={16} />
						</Button>
					) : (
						<Button className={styles.blank__head__button} size="l" onClick={handleClick}>
							<Icon data={FilePlus} size={16} />
							{ADD_BLANK}
						</Button>
					)}

					{isAdaptive ? (
						<Button
							ref={popupButtonRef}
							onClick={() => setOpenPopup((prevOpen) => !prevOpen)}
							className={styles.blank__head__button__popup}
							size="l"
							view="flat"
						>
							<Icon data={ArrowDownToLine} size={16} />
						</Button>
					) : (
						<Button
							ref={popupButtonRef}
							onClick={() => setOpenPopup((prevOpen) => !prevOpen)}
							className={styles.blank__head__button__popup}
							size="l"
						>
							<Icon data={ArrowDownToLine} size={16} />
							{DOWNLOAD_ALL}
						</Button>
					)}

					<Popup
						className={styles.blank__head__popup}
						anchorRef={popupButtonRef}
						open={openPopup}
						onClose={() => setOpenPopup(false)}
						placement="bottom"
					>
						<Button
							className={styles.blank__head__popup__button}
							view="flat"
							size="l"
							onClick={handleDownload}
						>
							{EXPORT_PDF}
						</Button>
					</Popup>
				</div>
			</div>
			<div className={styles.blank__main}>
				{isLoading && <BlankTableLoader isAdaptive={isAdaptive} />}

				{!isLoading && !accounts && inputValue?.length === 0 && (
					<div className={styles.blank__main__empty}>
						<Folder />
						<h3>{NO_DATA}</h3>
						<p>{NO_DATA_INFO}</p>
					</div>
				)}

				{!isLoading && accounts?.length !== 0 && (
					<>
						{isAdaptive ? (
							<AdaptiveBlankList
								data={accounts || []}
								refetch={refetch}
								pageSize={pageSize}
								setPageSize={setPageSize}
								pageIndex={pageIndex}
								setPageIndex={setPageIndex}
								totalItems={totalItems}
								sorting={sorting}
								setSorting={setSorting}
								role={role}
							/>
						) : (
							<div className={styles.blank__main__table}>
								<BlankTable
									data={accounts || []}
									refetch={refetch}
									pageSize={pageSize}
									setPageSize={setPageSize}
									pageIndex={pageIndex}
									setPageIndex={setPageIndex}
									totalItems={totalItems}
									sorting={sorting}
									setSorting={setSorting}
									role={role}
								/>
							</div>
						)}
					</>
				)}

				{!isLoading && accounts?.length == 0 && inputValue?.length !== 0 && (
					<div className={styles.blank__main__empty}>
						<NoSearchResults />
						<h3>{NO_DATA_ORG}</h3>
						<p>{NO_DATA_ORG_INFO}</p>
						<Button onClick={() => setInputValue('')} view="normal" size="l">
							{RESET_SEARCH}
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};
